import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchOutbidEngagements,
  type OutbidEngagementEntry,
  type OutbidEngagementOutcome,
} from "../lib/api";
import { relativeFromNow } from "../lib/format";
import { useDocumentTitle } from "../hooks/use-document-title";

const PAGE_SIZE = 50;

export function outcomeBadge(outcome: OutbidEngagementOutcome): string {
  switch (outcome) {
    case "success":
      return "badge badge-success";
    case "failed":
      return "badge badge-error";
    case "pending":
      return "badge badge-warning";
    default:
      return "badge badge-neutral";
  }
}

export const OUTCOME_LABELS: Record<OutbidEngagementOutcome, string> = {
  success: "success",
  failed: "failed",
  pending: "not confirmed",
};

export function formatAmount(amount: number | null, currency: string | null): string {
  if (amount === null) return "—";
  return currency ? `${currency} ${amount.toLocaleString()}` : amount.toLocaleString();
}

export function OutbidEngagementTable({
  items,
  showDealer,
  onSelect,
}: {
  items: OutbidEngagementEntry[];
  showDealer: boolean;
  onSelect: (engagement: OutbidEngagementEntry) => void;
}) {
  return (
    <table>
      <thead>
        <tr>
          <th>Outbid at</th>
          <th>Vehicle</th>
          <th>Project ID</th>
          {showDealer && <th>Dealer</th>}
          <th>Outbid on</th>
          <th>New bid</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.id} className="clickable-row" onClick={() => onSelect(item)}>
            <td className="mono" title={new Date(item.outbidAt).toLocaleString()}>
              {relativeFromNow(item.outbidAt)}
            </td>
            <td>
              <div>{item.vehicleTitle ?? `Project ${item.projectId}`}</div>
              {item.vehicleVin && (
                <div className="mono" style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                  {item.vehicleVin}
                </div>
              )}
            </td>
            <td className="mono">{item.projectId}</td>
            {showDealer && (
              <td onClick={(e) => e.stopPropagation()}>
                <Link to={`/dealer/${item.dealerId}`} className="dealer-link">
                  {item.dealerName ?? `#${item.dealerId}`}
                </Link>
              </td>
            )}
            <td className="mono">{formatAmount(item.outbidOnAmount, item.currency)}</td>
            <td className="mono">{formatAmount(item.newBidAmount, item.currency)}</td>
            <td>
              <span className={outcomeBadge(item.outcome)}>{OUTCOME_LABELS[item.outcome]}</span>
              {item.unsuccessfulReason && (
                <span
                  style={{
                    marginLeft: 6,
                    fontSize: 11,
                    color: "var(--color-text-secondary)",
                  }}
                >
                  {item.unsuccessfulReason}
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function OutbidEngagementPage() {
  useDocumentTitle("Outbid Engagement");
  const [filterDealer, setFilterDealer] = useState("");
  const [items, setItems] = useState<OutbidEngagementEntry[]>([]);
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<OutbidEngagementEntry | null>(null);

  const loadPage = useCallback(
    async (cursor: string | undefined) => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchOutbidEngagements({
          dealerId: filterDealer || undefined,
          cursor,
          limit: PAGE_SIZE,
        });
        setItems(result.items);
        setNextCursor(result.nextCursor);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    },
    [filterDealer],
  );

  useEffect(() => {
    setPageIndex(0);
    setCursors([null]);
    void loadPage(undefined);
  }, [loadPage]);

  function handleNext() {
    if (!nextCursor) return;
    const newPageIndex = pageIndex + 1;
    const newCursors = [...cursors];
    if (newCursors.length <= newPageIndex) {
      newCursors.push(nextCursor);
    }
    setCursors(newCursors);
    setPageIndex(newPageIndex);
    void loadPage(nextCursor);
  }

  function handlePrev() {
    if (pageIndex <= 0) return;
    const newPageIndex = pageIndex - 1;
    setPageIndex(newPageIndex);
    void loadPage(cursors[newPageIndex] ?? undefined);
  }

  return (
    <>
      <div className="card-header">
        <h1 className="page-title">Outbid Engagement</h1>
        <button
          className="btn btn-sm"
          type="button"
          onClick={() => {
            setPageIndex(0);
            setCursors([null]);
            void loadPage(undefined);
          }}
        >
          Refresh
        </button>
      </div>

      <div className="filter-bar">
        <input
          type="text"
          placeholder="Filter by dealer ID"
          value={filterDealer}
          onChange={(e) => setFilterDealer(e.target.value)}
          style={{ width: 160 }}
        />
      </div>

      {loading && <div className="loading">Loading outbid engagements...</div>}
      {error && <div className="error-msg">{error}</div>}

      {!loading && items.length === 0 && (
        <div className="empty-state">No outbid engagements found</div>
      )}

      {items.length > 0 && (
        <>
          <OutbidEngagementTable items={items} showDealer onSelect={setSelected} />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 12,
              fontSize: 13,
            }}
          >
            <button
              className="btn btn-sm"
              type="button"
              onClick={handlePrev}
              disabled={pageIndex === 0}
            >
              Previous
            </button>
            <span style={{ color: "var(--color-text-secondary)" }}>
              Page {pageIndex + 1}
            </span>
            <button
              className="btn btn-sm"
              type="button"
              onClick={handleNext}
              disabled={!nextCursor}
            >
              Next
            </button>
          </div>
        </>
      )}

      {selected && (
        <OutbidEngagementDrawer engagement={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
}

export function OutbidEngagementDrawer({
  engagement,
  onClose,
}: {
  engagement: OutbidEngagementEntry;
  onClose: () => void;
}) {
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="drawer">
        <div className="drawer-header">
          <div>
            <div className="drawer-title">
              {engagement.vehicleTitle ?? `Project ${engagement.projectId}`}
            </div>
            <div className="drawer-subtitle mono">
              {new Date(engagement.respondedAt).toLocaleString()}
            </div>
          </div>
          <button
            type="button"
            className="drawer-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="drawer-body">
          <div className="drawer-section">
            <div className="drawer-section-title">Summary</div>
            <div className="drawer-summary-grid">
              <div className="drawer-summary-label">Status</div>
              <div>
                <span className={outcomeBadge(engagement.outcome)}>
                  {OUTCOME_LABELS[engagement.outcome]}
                </span>
                {engagement.unsuccessfulReason && (
                  <span className="drawer-outcome-reason">{engagement.unsuccessfulReason}</span>
                )}
              </div>
              {engagement.errorMessage && (
                <>
                  <div className="drawer-summary-label">Error</div>
                  <div className="drawer-mono-wrap">{engagement.errorMessage}</div>
                </>
              )}
              <div className="drawer-summary-label">Dealer</div>
              <div>
                <Link to={`/dealer/${engagement.dealerId}`} className="dealer-link">
                  {engagement.dealerName ?? `#${engagement.dealerId}`}
                </Link>
              </div>
              <div className="drawer-summary-label">Project ID</div>
              <div className="mono">{engagement.projectId}</div>
              <div className="drawer-summary-label">VIN</div>
              <div className="mono">{engagement.vehicleVin ?? "—"}</div>
              <div className="drawer-summary-label">Outbid at</div>
              <div className="mono">{new Date(engagement.outbidAt).toLocaleString()}</div>
              <div className="drawer-summary-label">Outbid on</div>
              <div className="mono">
                {formatAmount(engagement.outbidOnAmount, engagement.currency)}
              </div>
              <div className="drawer-summary-label">Responded at</div>
              <div className="mono">{new Date(engagement.respondedAt).toLocaleString()}</div>
              <div className="drawer-summary-label">New bid</div>
              <div className="mono">
                {formatAmount(engagement.newBidAmount, engagement.currency)}
              </div>
              <div className="drawer-summary-label">Alert event ID</div>
              <div className="mono drawer-mono-wrap">{engagement.id}</div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
